import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reviewsApi, goalsApi, competenciesApi, usersApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Save, Users, Star, Award, TrendingUp } from 'lucide-react';

const RatingSelector = ({ value, onChange, disabled }) => {
  const ratings = [1, 2, 3, 4, 5];
  const colors = ['bg-red-400', 'bg-yellow-500', 'bg-blue-400', 'bg-green-500', 'bg-teal-500'];

  return (
    <div className="flex gap-2">
      {ratings.map((rating, idx) => (
        <button
          key={rating}
          type="button"
          onClick={() => !disabled && onChange(rating)}
          disabled={disabled}
          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
            value === rating ? `${colors[idx]} text-white` : 'bg-muted hover:bg-muted/80'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {rating}
        </button>
      ))}
    </div>
  );
};

export default function YearEndReviewPage() {
  const { user, isManager, isAdmin, isHR } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [goals, setGoals] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [review, setReview] = useState(null);
  const [goalRatings, setGoalRatings] = useState({});
  const [competencyRatings, setCompetencyRatings] = useState({});
  const [careerDevelopment, setCareerDevelopment] = useState('');
  
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    loadCompetencies();
    if (isManager || isAdmin || isHR) {
      loadTeamMembers();
    } else {
      loadMyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCompetencies = async () => {
    try {
      const response = await competenciesApi.getAll();
      setCompetencies(response.data);
    } catch (error) {
      console.error('Failed to load competencies:', error);
    }
  };

  const loadTeamMembers = async () => {
    try {
      let response;
      if (isAdmin || isHR) {
        response = await usersApi.getAll();
      } else {
        response = await usersApi.getTeam(user.id);
      }
      setTeamMembers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load team:', error);
      setLoading(false);
    }
  };

  const loadMyData = async () => {
    try {
      const goalsRes = await goalsApi.getAll({ year: currentYear });
      setGoals(goalsRes.data);
      
      const ratings = {};
      goalsRes.data.forEach(goal => {
        ratings[goal.id] = {
          rating: goal.rating || null,
          employee_update: goal.employee_update || '',
          manager_feedback: goal.manager_feedback || '',
        };
      });
      setGoalRatings(ratings);
      
      const reviewRes = await reviewsApi.create({
        employee_id: user.id,
        year: currentYear,
        stage: 'year_end',
      });
      setReview(reviewRes.data);
      setCareerDevelopment(reviewRes.data.career_development || '');
      
      // Initialize competency ratings
      const compRatings = {};
      (reviewRes.data.competency_ratings || []).forEach(cr => {
        compRatings[cr.competency_id] = {
          rating: cr.rating,
          feedback: cr.feedback || '',
        };
      });
      setCompetencyRatings(compRatings);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMemberData = async (memberId) => {
    setLoading(true);
    try {
      const goalsRes = await goalsApi.getAll({ employee_id: memberId, year: currentYear });
      setGoals(goalsRes.data);
      
      const ratings = {};
      goalsRes.data.forEach(goal => {
        ratings[goal.id] = {
          rating: goal.rating || null,
          employee_update: goal.employee_update || '',
          manager_feedback: goal.manager_feedback || '',
        };
      });
      setGoalRatings(ratings);
      
      const reviewRes = await reviewsApi.create({
        employee_id: memberId,
        year: currentYear,
        stage: 'year_end',
      });
      setReview(reviewRes.data);
      setCareerDevelopment(reviewRes.data.career_development || '');
      
      const compRatings = {};
      (reviewRes.data.competency_ratings || []).forEach(cr => {
        compRatings[cr.competency_id] = {
          rating: cr.rating,
          feedback: cr.feedback || '',
        };
      });
      setCompetencyRatings(compRatings);
    } catch (error) {
      console.error('Failed to load member data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    loadMemberData(member.id);
  };

  const handleSaveGoalRating = async (goalId) => {
    setSaving(true);
    try {
      await goalsApi.update(goalId, {
        rating: goalRatings[goalId].rating,
        employee_update: goalRatings[goalId].employee_update,
        manager_feedback: goalRatings[goalId].manager_feedback,
      });
      toast.success('Goal rating saved');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCompetencyRatings = async () => {
    if (!review) return;
    
    setSaving(true);
    try {
      const ratings = competencies.map(comp => ({
        competency_id: comp.id,
        competency_name: comp.name,
        rating: competencyRatings[comp.id]?.rating || 0,
        feedback: competencyRatings[comp.id]?.feedback || '',
      }));
      
      await reviewsApi.update(review.id, {
        competency_ratings: ratings,
      });
      toast.success('Competency ratings saved');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCareerDevelopment = async () => {
    if (!review) return;
    
    setSaving(true);
    try {
      await reviewsApi.update(review.id, {
        career_development: careerDevelopment,
      });
      toast.success('Career development saved');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const calculateGoalScore = () => {
    let totalScore = 0;
    let totalWeight = 0;
    
    goals.forEach(goal => {
      const rating = goalRatings[goal.id]?.rating;
      if (rating) {
        totalScore += (goal.weight / 100) * rating;
        totalWeight += goal.weight;
      }
    });
    
    return totalWeight > 0 ? totalScore.toFixed(2) : '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if ((isManager || isAdmin || isHR) && !selectedMember) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="year-end-review-page">
        <div>
          <h1 className="text-3xl font-bold">Year-End Review</h1>
          <p className="text-muted-foreground mt-1">
            Select a team member to conduct their year-end review
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamMembers.map((member) => (
                <button
                  key={member.id}
                  onClick={() => handleSelectMember(member)}
                  className="p-4 border rounded-lg hover:bg-muted/50 transition-colors text-left"
                  data-testid={`select-member-yearend-${member.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="font-medium text-primary">
                        {member.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-muted-foreground">{member.employee_number}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="year-end-review-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Year-End Review</h1>
          <p className="text-muted-foreground mt-1">
            {selectedMember ? `Reviewing: ${selectedMember.name}` : 'Complete your year-end performance review'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Goal Score</p>
            <p className="text-2xl font-bold">{calculateGoalScore()}</p>
          </div>
          {selectedMember && (
            <Button variant="outline" onClick={() => setSelectedMember(null)}>
              Back to Team
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="goals" className="space-y-6">
        <TabsList>
          <TabsTrigger value="goals">
            <Star className="h-4 w-4 mr-2" />
            Goal Ratings
          </TabsTrigger>
          <TabsTrigger value="competencies">
            <Award className="h-4 w-4 mr-2" />
            Competencies
          </TabsTrigger>
          <TabsTrigger value="development">
            <TrendingUp className="h-4 w-4 mr-2" />
            Career Development
          </TabsTrigger>
        </TabsList>

        <TabsContent value="goals" className="space-y-4">
          {goals.map((goal) => (
            <Card key={goal.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{goal.title}</CardTitle>
                    <CardDescription>{goal.kpi}</CardDescription>
                  </div>
                  <Badge variant="outline">{goal.weight}% Weight</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Employee Year-End Update</Label>
                  <Textarea
                    value={goalRatings[goal.id]?.employee_update || ''}
                    onChange={(e) => setGoalRatings({
                      ...goalRatings,
                      [goal.id]: { ...goalRatings[goal.id], employee_update: e.target.value }
                    })}
                    placeholder="Describe your achievements..."
                    disabled={isManager || isAdmin || isHR}
                    data-testid={`yearend-employee-update-${goal.id}`}
                  />
                </div>
                
                {(isManager || isAdmin || isHR) && (
                  <>
                    <div className="space-y-2">
                      <Label>Rating</Label>
                      <RatingSelector
                        value={goalRatings[goal.id]?.rating}
                        onChange={(rating) => setGoalRatings({
                          ...goalRatings,
                          [goal.id]: { ...goalRatings[goal.id], rating }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Manager Feedback & Justification</Label>
                      <Textarea
                        value={goalRatings[goal.id]?.manager_feedback || ''}
                        onChange={(e) => setGoalRatings({
                          ...goalRatings,
                          [goal.id]: { ...goalRatings[goal.id], manager_feedback: e.target.value }
                        })}
                        placeholder="Provide justification for the rating..."
                        data-testid={`yearend-manager-feedback-${goal.id}`}
                      />
                    </div>
                  </>
                )}
                
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => handleSaveGoalRating(goal.id)}
                    disabled={saving}
                    data-testid={`save-goal-rating-${goal.id}`}
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="competencies" className="space-y-4">
          {competencies.map((comp) => (
            <Card key={comp.id}>
              <CardHeader>
                <CardTitle className="text-lg">{comp.name}</CardTitle>
                <CardDescription>{comp.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Rating</Label>
                  <RatingSelector
                    value={competencyRatings[comp.id]?.rating}
                    onChange={(rating) => setCompetencyRatings({
                      ...competencyRatings,
                      [comp.id]: { ...competencyRatings[comp.id], rating }
                    })}
                    disabled={!isManager && !isAdmin && !isHR}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Feedback</Label>
                  <Textarea
                    value={competencyRatings[comp.id]?.feedback || ''}
                    onChange={(e) => setCompetencyRatings({
                      ...competencyRatings,
                      [comp.id]: { ...competencyRatings[comp.id], feedback: e.target.value }
                    })}
                    placeholder="Provide feedback on this competency..."
                    disabled={!isManager && !isAdmin && !isHR}
                    data-testid={`competency-feedback-${comp.id}`}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
          
          {(isManager || isAdmin || isHR) && (
            <div className="flex justify-end">
              <Button onClick={handleSaveCompetencyRatings} disabled={saving} data-testid="save-competency-ratings-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Competency Ratings
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="development">
          <Card>
            <CardHeader>
              <CardTitle>Career & Development Needs</CardTitle>
              <CardDescription>
                Discuss career aspirations and developmental needs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={careerDevelopment}
                onChange={(e) => setCareerDevelopment(e.target.value)}
                placeholder="Document career goals, training needs, and development plans..."
                rows={8}
                data-testid="career-development-input"
              />
              <div className="flex justify-end">
                <Button onClick={handleSaveCareerDevelopment} disabled={saving} data-testid="save-career-development-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
